import AutoAwesomeOutlinedIcon from '@mui/icons-material/AutoAwesomeOutlined';
import { DialogActions, DialogTitle, Grid } from '@mui/material';
import Autocomplete from '@mui/material/Autocomplete';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import TextField from '@mui/material/TextField';
import React, { ChangeEvent, SyntheticEvent, useState } from 'react';
import { toast } from 'react-toastify';

import { EmailAiSuggestionButtonFormModel } from '@/components/emailWindow/components/newEmail/components/aiSuggestionButton/components/dialog/model/EmailAiSuggestionButtonForm.model.ts';
import { CheckboxFormField } from '@/components/form/CheckboxField.comp.tsx';
import { FormFieldLabel } from '@/components/form/FormFieldLabel.comp.tsx';
import { ToastConfig } from '@/config/ToastConfig.util.ts';
import { useGetSelectOptions } from '@/models/useGetSelectOptions.util';
import { useTranslationWithPrefix } from '@/utils/useTranslationWithPrefix';

import { useGetEmailAiSuggestion } from '../../../../util/useGetEmailAiSuggestion.util';

type Props = {
  emailId: string;
  onClose: (refetch: boolean) => void;
};

export const NewEmailAiSuggestionButtonDialog: React.FC<Props> = ({ emailId, onClose }) => {
  const { t } = useTranslationWithPrefix('views.emails.newEmail.body.dialog.aiSuggestion');
  const { getEmailAiSuggestion, isGettingEmailAiSuggestion } = useGetEmailAiSuggestion();
  const { emailAttachmentsTypeOptions, emailContentTypeOptions } = useGetSelectOptions();
  const [data, setData] = useState<EmailAiSuggestionButtonFormModel>({
    addAttachments: [],
    addCafeRoyalInfo: false,
    contentType: emailContentTypeOptions.find(it => it.value === 'CUSTOM')!,
    instruction: '',
  });

  const isCustomTypeSelected = data.contentType.value === 'CUSTOM';

  const handleSubmit = async () => {
    if (isCustomTypeSelected && (data.instruction?.length ?? 0) < 3) {
      toast(t('toast.provideInstructions'), ToastConfig.warning);
      return;
    }
    await getEmailAiSuggestion({
      dto: {
        ...data,
        addAttachments: data.addAttachments.map(it => it.value),
        contentType: data.contentType.value,
      },
      emailId,
    });
    onClose(true);
  };

  const handleChange =
    (field: keyof EmailAiSuggestionButtonFormModel) => (e: ChangeEvent<HTMLInputElement>) => {
      setData(cur => ({
        ...cur,
        [field]: e.target.value,
      }));
    };

  const handleChangeBoolean = (_event: SyntheticEvent<Element, Event>, checked: boolean) => {
    setData(cur => ({
      ...cur,
      addCafeRoyalInfo: checked,
    }));
  };

  return (
    <Dialog disablePortal open>
      <DialogTitle />
      <DialogContent>
        <Grid
          container
          spacing={2}
          sx={{
            rowGap: 3,
          }}
        >
          <Grid size={8}>
            <Box
              sx={{
                position: 'relative',
                pt: 2,
              }}
            >
              <FormFieldLabel isRequired label={t('form.contentType')} sx={{ top: -10 }} />
              <Autocomplete
                disableClearable
                disabled={isGettingEmailAiSuggestion}
                fullWidth
                getOptionLabel={option => option.label}
                onChange={(_, newValue) => {
                  if (newValue === null) return;
                  setData(cur => ({ ...cur, contentType: newValue }));
                }}
                options={emailContentTypeOptions}
                renderInput={params => (
                  <TextField
                    {...params}
                    hiddenLabel
                    placeholder={t('form.contentType')}
                    size="small"
                    variant="standard"
                  />
                )}
                slotProps={{
                  listbox: { sx: { maxHeight: 200, p: 0.5 } },
                  popper: { style: { zIndex: 1500 } },
                }}
                value={data.contentType}
              />
            </Box>
          </Grid>
          <Grid
            size={4}
            sx={{
              alignContent: 'center',
            }}
          >
            <CheckboxFormField
              isDisabled={isGettingEmailAiSuggestion}
              label={t('form.addCafeRoyalInfo')}
              labelPlacement="end"
              onChange={handleChangeBoolean}
              value={data.addCafeRoyalInfo}
            />
          </Grid>
          <Grid size={12}>
            <Box
              sx={{
                position: 'relative',
                pt: 2,
              }}
            >
              <FormFieldLabel
                isRequired={false}
                label={t('form.addAttachments')}
                sx={{ top: -10 }}
              />
              <Autocomplete
                disableCloseOnSelect
                disabled={isGettingEmailAiSuggestion}
                filterSelectedOptions
                fullWidth
                getOptionLabel={option => option.label}
                multiple
                onChange={(_, newValue) => {
                  setData(cur => ({ ...cur, addAttachments: newValue }));
                }}
                options={emailAttachmentsTypeOptions}
                renderInput={params => (
                  <TextField
                    {...params}
                    hiddenLabel
                    placeholder={t('form.addAttachments')}
                    size="small"
                    variant="standard"
                  />
                )}
                slotProps={{
                  listbox: { sx: { maxHeight: 200, p: 0.5 } },
                  popper: { style: { zIndex: 1500 } },
                }}
                value={data.addAttachments}
              />
            </Box>
          </Grid>
          <Grid size={12}>
            <Box
              sx={{
                position: 'relative',
                pt: 2,
              }}
            >
              <FormFieldLabel
                isRequired={isCustomTypeSelected}
                label={t('form.instruction')}
                sx={{ top: -10 }}
              />
              <TextField
                disabled={isGettingEmailAiSuggestion}
                fullWidth
                minRows={4}
                multiline
                onChange={handleChange('instruction')}
                placeholder={t('form.instructionPlaceholder')}
                value={data.instruction}
              />
            </Box>
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button
          disabled={isGettingEmailAiSuggestion}
          onClick={() => onClose(false)}
          variant="outlined"
        >
          {t('button.cancel')}
        </Button>
        <Button
          endIcon={<AutoAwesomeOutlinedIcon />}
          loading={isGettingEmailAiSuggestion}
          onClick={handleSubmit}
        >
          {t('button.submit')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
